import { useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Rectangle,
} from 'recharts'
import { ChartEmptyState } from './ChartEmptyState'

export type ChartTheme = 'orange' | 'blue' | 'purple'

interface ThemeColors {
  normalStart: string
  normalEnd: string
  activeStart: string
  activeEnd: string
  legend: string
}

const THEME_CONFIGS: Record<ChartTheme, ThemeColors> = {
  orange: {
    normalStart: '#FFD2AD',
    normalEnd: '#F29141',
    activeStart: '#F29141',
    activeEnd: '#B85400',
    legend: '#F29141',
  },
  blue: {
    normalStart: '#59B6FF',
    normalEnd: '#59B6FF',
    activeStart: '#55A9EA',
    activeEnd: '#08548F',
    legend: '#58B3FF',
  },
  purple: {
    normalStart: '#B089F0',
    normalEnd: '#B089F0',
    activeStart: '#55A9EA',
    activeEnd: '#6A25DD',
    legend: '#A06BFF',
  },
}

const MIN_X_TICK_CHARS = 8
const MAX_X_TICK_CHARS = 15
const LABEL_FONT_SIZE = 11
const AVERAGE_CHAR_WIDTH = 6.2

function truncateUnitName(value: string, maxChars: number) {
  if (!value || value.length <= maxChars) return value
  return `${value.slice(0, maxChars)}...`
}

interface XAxisTickProps {
  x?: number
  y?: number
  maxChars?: number
  payload?: { value?: string }
}

function CustomXAxisTick({
  x = 0,
  y = 0,
  maxChars = MAX_X_TICK_CHARS,
  payload,
}: XAxisTickProps) {
  const fullText = payload?.value ?? ''
  const displayText = truncateUnitName(fullText, maxChars)

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={14}
        textAnchor="middle"
        fontSize={11}
        fill="#909090"
      >
        {displayText}
        <title>{fullText}</title>
      </text>
    </g>
  )
}

interface ActiveBarProps {
  x?: number
  y?: number
  width?: number
  height?: number
  index?: number
  activeBarIndex: number | null
  normalGradientId: string
  activeGradientId: string
  activeType?: 'badge' | 'dot'
  value?: number | string
}

const CustomActiveBar = ({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  index,
  activeBarIndex,
  normalGradientId,
  activeGradientId,
  activeType = 'badge',
  value,
}: ActiveBarProps) => {
  const isActive = Number(index) === activeBarIndex

  return (
    <g>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={
          isActive ? `url(#${activeGradientId})` : `url(#${normalGradientId})`
        }
        radius={[4, 4, 0, 0]}
      />
      {isActive && activeType === 'dot' && (
        <circle
          cx={x + width / 2}
          cy={y}
          r={5}
          fill="#C6621B"
          stroke="#fff"
          strokeWidth={2}
        />
      )}
      {isActive && activeType === 'badge' && value !== undefined && (
        <g transform={`translate(${x + width / 2}, ${y - 12})`}>
          <rect
            x={-18}
            y={-16}
            width={36}
            height={22}
            rx={11}
            fill="#FFFFFF"
            filter="drop-shadow(0px 2px 4px rgba(0,0,0,0.12))"
          />
          <text
            x={0}
            y={-1}
            textAnchor="middle"
            fontSize={12}
            fontWeight="600"
            fill="#555555"
          >
            {value}
          </text>
        </g>
      )}
    </g>
  )
}

export interface CommonBarChartProps<T> {
  title: string
  data: T[]
  xKey: Extract<keyof T, string>
  yKey: Extract<keyof T, string>
  theme?: ChartTheme
  legendLabel?: string
  activeType?: 'badge' | 'dot'
  sortDescending?: boolean
  barSize?: number
  height?: number
}

export function CommonBarChart<T extends Record<string, any>>({
  title,
  data = [],
  xKey,
  yKey,
  theme = 'blue',
  legendLabel,
  activeType = 'badge',
  sortDescending = false,
  barSize = 36,
  height = 300,
}: CommonBarChartProps<T>) {
  const rawId = useId()
  const uniqueId = rawId.replaceAll(':', '')
  const normalGradientId = `normalBarGradient_${uniqueId}`
  const activeGradientId = `activeBarGradient_${uniqueId}`

  const chartWrapperRef = useRef<HTMLDivElement | null>(null)
  const [chartWidth, setChartWidth] = useState(0)
  const [activeBarIndex, setActiveBarIndex] = useState<number | null>(null)
  const barPositions = useRef<{ x: number; width: number; value: any }[]>([])

  const themeColors = THEME_CONFIGS[theme]

  const processedData = useMemo(() => {
    const safeData = data
    if (!sortDescending) return safeData
    return [...safeData].sort((a, b) => (b[yKey] ?? 0) - (a[yKey] ?? 0))
  }, [data, yKey, sortDescending])

  useEffect(() => {
    const chartWrapper = chartWrapperRef.current
    if (!chartWrapper) return

    const updateWidth = () => setChartWidth(chartWrapper.clientWidth)
    updateWidth()

    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(chartWrapper)

    return () => resizeObserver.disconnect()
  }, [])

  const maxTickChars = useMemo(() => {
    const itemCount = Math.max(processedData.length, 1)
    const widthPerTick = chartWidth > 0 ? chartWidth / itemCount : 0
    const calculatedChars = Math.floor(
      (widthPerTick - LABEL_FONT_SIZE) / AVERAGE_CHAR_WIDTH,
    )

    return Math.min(
      MAX_X_TICK_CHARS,
      Math.max(MIN_X_TICK_CHARS, calculatedChars),
    )
  }, [chartWidth, processedData.length])

  const hasData = processedData.length > 0

  return (
    <div className="flex h-full w-full flex-col rounded-xl border border-solid border-gray-300 bg-white p-5 shadow-sm">
      <div className="mb-3 ml-4 flex items-center justify-between">
        <h3 className="text-[18px] font-bold uppercase text-[#1F2937]">
          {title}
        </h3>
      </div>

      <div
        ref={chartWrapperRef}
        role="img"
        aria-label={title}
        className="relative w-full min-w-0"
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          const mouseX = e.clientX - rect.left

          const foundIndex = barPositions.current.findIndex(
            (pos) => mouseX >= pos.x - 15 && mouseX <= pos.x + pos.width + 15,
          )

          setActiveBarIndex(foundIndex !== -1 ? foundIndex : null)
        }}
        onMouseLeave={() => setActiveBarIndex(null)}
        onBlur={() => setActiveBarIndex(null)}
      >
        {hasData ? (
          <ResponsiveContainer
            width="100%"
            height={height}
            initialDimension={{ width: 340, height: height }}
          >
            <BarChart
              data={processedData}
              margin={{ top: 32, right: 10, bottom: 5, left: -10 }}
              onMouseLeave={() => setActiveBarIndex(null)}
            >
              <defs>
                <linearGradient
                  id={normalGradientId}
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                  gradientUnits="objectBoundingBox"
                >
                  <stop offset="0%" stopColor={themeColors.normalStart} />
                  <stop offset="100%" stopColor={themeColors.normalEnd} />
                </linearGradient>

                <linearGradient
                  id={activeGradientId}
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                  gradientUnits="objectBoundingBox"
                >
                  <stop offset="0%" stopColor={themeColors.activeStart} />
                  <stop offset="100%" stopColor={themeColors.activeEnd} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#E5E7EB"
              />

              <XAxis
                dataKey={xKey as string}
                tick={<CustomXAxisTick maxChars={maxTickChars} />}
                interval={0}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
              />

              <YAxis
                tick={{ fontSize: 11, fill: '#909090' }}
                tickLine={false}
                axisLine={false}
              />

              <Tooltip
                cursor={false}
                active={activeBarIndex !== null}
                wrapperStyle={{ pointerEvents: 'none' }}
                content={({ active, payload }) => {
                  if (active && payload.length > 0) {
                    const rawData = payload[0].payload
                    return (
                      <div className="rounded border bg-white p-2 text-xs shadow-lg">
                        <div className="font-bold">{rawData[xKey]}</div>
                        <div>
                          {legendLabel || 'Giá trị'}: {rawData[yKey]}
                        </div>
                      </div>
                    )
                  }
                  return null
                }}
              />

              <Bar
                dataKey={yKey as string}
                barSize={barSize}
                className={activeBarIndex !== null ? 'cursor-pointer' : ''}
                shape={(props: any) => {
                  const { x, width, index, value } = props
                  barPositions.current[index] = { x, width, value }
                  return (
                    <CustomActiveBar
                      {...props}
                      value={value}
                      activeBarIndex={activeBarIndex}
                      normalGradientId={normalGradientId}
                      activeGradientId={activeGradientId}
                      activeType={activeType}
                    />
                  )
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ height: `${height}px` }}>
            <ChartEmptyState variant="grid" gridRows={5} />
          </div>
        )}
      </div>

      {legendLabel && (
        <div className="mt-2 flex items-center justify-center gap-2 text-xs font-semibold">
          <span
            className="h-3 w-3 rounded-sm"
            style={{ backgroundColor: themeColors.legend }}
          />
          <span style={{ color: themeColors.legend }}>{legendLabel}</span>
        </div>
      )}
    </div>
  )
}
